import { Zap, Mail, Clock, GitBranch, Target, Users, TrendingUp, Settings2, Play, Pause } from "lucide-react";
import Badge from "../../../components/ui/Badge";
import { AUTOMATION_STATUS, STEP_TYPES } from "./automation.data";

const MINI_ICON = { trigger: Zap, email: Mail, wait: Clock, condition: GitBranch, goal: Target };

/** Compact horizontal step pipeline preview. */
function MiniPipeline({ steps }) {
  const shown = steps.slice(0, 5);
  const extra = steps.length - shown.length;
  return (
    <div className="flex items-center gap-1">
      {shown.map((s, i) => {
        const cfg = STEP_TYPES[s.type] ?? STEP_TYPES.email;
        const Icon = MINI_ICON[s.type] ?? Mail;
        return (
          <div key={i} className="flex items-center gap-1">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: cfg.bg }} title={cfg.label}>
              <Icon className="w-3 h-3" style={{ color: cfg.color }} />
            </div>
            {i < shown.length - 1 && <span className="w-2 h-px" style={{ background: "var(--color-border-strong)" }} />}
          </div>
        );
      })}
      {extra > 0 && <span className="text-[10px] font-bold text-text_muted ml-1">+{extra}</span>}
    </div>
  );
}

export default function AutomationCard({ automation: a, onOpen, onToggle }) {
  const st = AUTOMATION_STATUS[a.status] ?? AUTOMATION_STATUS.draft;
  const isActive = a.status === "active";

  return (
    <div
      className="bg-surface border border-border rounded-xl p-5 flex flex-col gap-4 transition-all duration-200 hover:-translate-y-0.5"
      style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button onClick={() => onOpen(a)} className="text-left">
            <h3 className="text-sm font-bold text-text_primary leading-tight hover:text-primary transition-colors truncate">{a.name}</h3>
          </button>
          <p className="text-xs text-text_muted mt-0.5 inline-flex items-center gap-1">
            <Zap className="w-3 h-3" /> {a.trigger}
          </p>
        </div>
        <Badge tone={st.tone} dot>{st.label}</Badge>
      </div>

      {/* Pipeline */}
      <div className="rounded-lg bg-surface_alt/50 border border-border px-3 py-2.5">
        <MiniPipeline steps={a.steps} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Users, label: "Enrolled", value: a.enrolled.toLocaleString(), color: "var(--color-primary)" },
          { icon: TrendingUp, label: "Conv.", value: `${a.convRate}%`, color: "var(--color-success)" },
          { icon: Target, label: "Revenue", value: a.revenue ? `$${(a.revenue / 1000).toFixed(1)}K` : "$0", color: "var(--color-secondary)" },
        ].map((m) => (
          <div key={m.label} className="text-center">
            <div className="text-sm font-bold text-text_primary leading-none">{m.value}</div>
            <div className="text-[10px] text-text_muted mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1 border-t border-border">
        <button
          onClick={() => onToggle(a)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-border text-text_secondary hover:bg-surface_alt transition-colors"
        >
          {isActive ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Activate</>}
        </button>
        <button
          onClick={() => onOpen(a)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors text-white"
          style={{ background: "var(--color-primary)" }}
        >
          <Settings2 className="w-3.5 h-3.5" /> Edit flow
        </button>
      </div>
    </div>
  );
}
